// apps/web/src/pages/Profile/components/BankAccountSection.tsx

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckCircle,
  CreditCard,
  Lock,
  Pencil,
  Plus,
  ShieldAlert,
  Star,
  Trash2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  useCreateMyBankAccount,
  useDeleteMyBankAccount,
  useMyBankAccounts,
  useUpdateMyBankAccount,
} from '@alamo/api';
import type { ProfileAccountType, ProfileBankAccountMasked, ProfilePixKeyType } from '@alamo/types';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Skeleton,
  Spinner,
  cn,
} from '@alamo/ui';
import { extractApiError, normalizeEmail } from '@alamo/utils';

import { BankPrimaryControl } from '../../../components/BankPrimaryControl';
import { nameSchema } from '../../../lib/nameSchema';

const ACCOUNT_TYPES: ProfileAccountType[] = ['checking', 'savings', 'payment'];
const PIX_KEY_TYPES: ProfilePixKeyType[] = ['cpf', 'cnpj', 'email', 'phone', 'random'];

export const bankAccountSchema = z
  .object({
    bankCode: z.string().min(1, 'common:validation.required'),
    bankName: z.string().min(1, 'common:validation.required'),
    agency: z.string().min(1, 'common:validation.required'),
    agencyDigit: z.string().optional(),
    accountNumber: z.string().min(1, 'common:validation.required'),
    accountDigit: z.string().min(1, 'common:validation.required'),
    accountType: z.enum(['checking', 'savings', 'payment']),
    holderName: nameSchema(1),
    holderDocument: z.string().optional(),
    pixKeyType: z.enum(['cpf', 'cnpj', 'email', 'phone', 'random']).optional(),
    pixKeyValue: z.string().optional(),
    isPrimary: z.boolean().optional(),
    currentPassword: z.string().min(1, 'common:validation.required'),
  })
  .transform((data) =>
    data.pixKeyType === 'email' && data.pixKeyValue !== undefined
      ? { ...data, pixKeyValue: normalizeEmail(data.pixKeyValue) }
      : data,
  )
  .superRefine((data, ctx) => {
    if (
      data.pixKeyType === 'email' &&
      data.pixKeyValue !== undefined &&
      data.pixKeyValue &&
      !z.email().safeParse(data.pixKeyValue).success
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['pixKeyValue'],
        message: 'common:validation.invalid_email',
      });
    }
  });

type BankAccountFormValues = z.infer<typeof bankAccountSchema>;

interface BankAccountDialogProps {
  open: boolean;
  onClose: () => void;
  account?: ProfileBankAccountMasked;
  accountCount: number;
  makePrimary?: boolean;
}

function BankAccountDialog({
  open,
  onClose,
  account,
  accountCount,
  makePrimary,
}: BankAccountDialogProps) {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const create = useCreateMyBankAccount();
  const update = useUpdateMyBankAccount();
  const isEditing = !!account;
  const automaticPrimary = account ? accountCount === 1 : accountCount === 0;
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    formState: { errors },
  } = useForm<BankAccountFormValues>({
    resolver: zodResolver(bankAccountSchema),
    defaultValues: {
      bankCode: account?.bankCode ?? '',
      bankName: account?.bankName ?? '',
      agency: '',
      agencyDigit: account?.agencyDigit ?? '',
      accountNumber: '',
      accountDigit: account?.accountDigit ?? '',
      accountType: account?.accountType ?? 'checking',
      holderName: account?.holderName ?? '',
      holderDocument: '',
      pixKeyType: account?.pixKeyType ?? undefined,
      pixKeyValue: '',
      isPrimary: automaticPrimary || makePrimary || account?.isPrimary || false,
      currentPassword: '',
    },
  });

  const currentAccountType = watch('accountType');
  const currentPixKeyType = watch('pixKeyType');
  const isPending = create.isPending || update.isPending;

  const onSubmit = (values: BankAccountFormValues) => {
    setPasswordError(null);

    const onSuccess = () => {
      toast.success(
        isEditing
          ? t('auth:profile.bank_accounts.toast.updated')
          : t('auth:profile.bank_accounts.toast.created'),
      );
      reset();
      onClose();
    };

    const onError = (err: unknown) => {
      const { status, code, message } = extractApiError(err);
      if (status === 401 && code === 'AUTH_INVALID_CREDENTIALS') {
        setPasswordError(t('auth:profile.bank_accounts.errors.wrong_password'));
        setError('currentPassword', {
          message: 'auth:profile.bank_accounts.errors.wrong_password',
        });
        return;
      }
      toast.error(
        t(`errors:${code ?? 'generic'}`, {
          defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
        }),
      );
    };

    if (isEditing && account) {
      update.mutate(
        {
          id: account.id,
          input: {
            bankCode: values.bankCode,
            bankName: values.bankName,
            agency: values.agency || undefined,
            agencyDigit: values.agencyDigit || undefined,
            accountNumber: values.accountNumber || undefined,
            accountDigit: values.accountDigit,
            accountType: values.accountType,
            holderName: values.holderName,
            holderDocument: values.holderDocument || undefined,
            pixKeyType: values.pixKeyType || undefined,
            pixKeyValue: values.pixKeyValue || undefined,
            isPrimary: automaticPrimary || values.isPrimary || false,
            currentPassword: values.currentPassword,
          },
        },
        { onSuccess, onError },
      );
    } else {
      create.mutate(
        {
          bankCode: values.bankCode,
          bankName: values.bankName,
          agency: values.agency,
          agencyDigit: values.agencyDigit || undefined,
          accountNumber: values.accountNumber,
          accountDigit: values.accountDigit,
          accountType: values.accountType,
          holderName: values.holderName,
          holderDocument: values.holderDocument || undefined,
          pixKeyType: values.pixKeyType || undefined,
          pixKeyValue: values.pixKeyValue || undefined,
          isPrimary: automaticPrimary || values.isPrimary || false,
          currentPassword: values.currentPassword,
        },
        { onSuccess, onError },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('auth:profile.bank_accounts.edit') : t('auth:profile.bank_accounts.add')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 overflow-y-auto max-h-[70vh] pr-1"
          noValidate
        >
          {/* Aviso de segurança */}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
            <ShieldAlert className="size-4 text-amber-600 mt-0.5 shrink-0" aria-hidden />
            <p className="text-xs text-amber-800">
              {t('auth:profile.bank_accounts.security_notice')}
            </p>
          </div>

          {/* Banco */}
          <div className="grid grid-cols-3 gap-3">
            <Input
              label={t('auth:profile.bank_accounts.fields.bank_code')}
              placeholder="341"
              {...register('bankCode')}
              error={errors.bankCode ? t(errors.bankCode.message as string) : undefined}
            />
            <div className="col-span-2">
              <Input
                label={t('auth:profile.bank_accounts.fields.bank_name')}
                placeholder="Itaú"
                {...register('bankName')}
                error={errors.bankName ? t(errors.bankName.message as string) : undefined}
              />
            </div>
          </div>

          {/* Tipo de conta */}
          <div>
            <label className="block text-sm font-body font-medium text-slate-700 mb-1">
              {t('auth:profile.bank_accounts.fields.account_type')}
            </label>
            <div className="flex gap-2">
              {ACCOUNT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setValue('accountType', type)}
                  className={cn(
                    'flex-1 px-3 py-2 rounded-md border text-sm font-body transition-colors min-h-[44px]',
                    currentAccountType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {t(`auth:profile.bank_accounts.type_${type}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Agência */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input
                label={t('auth:profile.bank_accounts.fields.agency')}
                placeholder={
                  isEditing
                    ? t('auth:profile.bank_accounts.masked_agency', {
                        last4: account?.agencyLast4 ?? '****',
                      })
                    : '0000'
                }
                {...register('agency')}
                error={errors.agency ? t(errors.agency.message as string) : undefined}
              />
            </div>
            <Input
              label={t('auth:profile.bank_accounts.fields.agency_digit')}
              placeholder="0"
              {...register('agencyDigit')}
            />
          </div>

          {/* Conta */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input
                label={t('auth:profile.bank_accounts.fields.account_number')}
                placeholder={
                  isEditing
                    ? t('auth:profile.bank_accounts.masked_account', {
                        last4: account?.accountNumberLast4 ?? '****',
                      })
                    : '00000000'
                }
                {...register('accountNumber')}
                error={errors.accountNumber ? t(errors.accountNumber.message as string) : undefined}
              />
            </div>
            <Input
              label={t('auth:profile.bank_accounts.fields.account_digit')}
              placeholder="0"
              {...register('accountDigit')}
              error={errors.accountDigit ? t(errors.accountDigit.message as string) : undefined}
            />
          </div>

          {/* Titular */}
          <Input
            label={t('auth:profile.bank_accounts.fields.holder_name')}
            {...register('holderName')}
            error={errors.holderName ? t(errors.holderName.message as string) : undefined}
          />
          <Input
            label={t('auth:profile.bank_accounts.fields.holder_document')}
            placeholder="000.000.000-00"
            {...register('holderDocument')}
          />

          {/* Pix (opcional) */}
          <div>
            <label className="block text-sm font-body font-medium text-slate-700 mb-1">
              {t('auth:profile.bank_accounts.fields.pix_key_type')}
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setValue('pixKeyType', undefined)}
                className={cn(
                  'px-3 py-2 rounded-md border text-sm font-body transition-colors min-h-[44px]',
                  !currentPixKeyType
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                Nenhuma
              </button>
              {PIX_KEY_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setValue('pixKeyType', type)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm font-body transition-colors min-h-[44px]',
                    currentPixKeyType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {t(`auth:profile.bank_accounts.pix_type_${type}`)}
                </button>
              ))}
            </div>
            {currentPixKeyType && (
              <div className="mt-3">
                <Input
                  label={t('auth:profile.bank_accounts.fields.pix_key_value')}
                  type={currentPixKeyType === 'email' ? 'email' : 'text'}
                  {...register('pixKeyValue')}
                />
              </div>
            )}
          </div>

          <BankPrimaryControl
            automatic={automaticPrimary}
            checked={Boolean(watch('isPrimary'))}
            onSelect={() => setValue('isPrimary', true, { shouldDirty: true })}
          />

          {/* Senha atual — campo obrigatório, sempre visível */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="size-4 text-slate-500" aria-hidden />
              <span className="text-sm font-body font-medium text-slate-700">
                {t('auth:profile.bank_accounts.fields.current_password')}
              </span>
            </div>
            <Input
              type="password"
              label={t('auth:profile.bank_accounts.fields.current_password')}
              required
              {...register('currentPassword')}
              error={
                errors.currentPassword
                  ? t(errors.currentPassword.message as string)
                  : (passwordError ?? undefined)
              }
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending}
              aria-busy={isPending || undefined}
            >
              {isPending && <Spinner size="sm" />}
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteBankAccountDialogProps {
  open: boolean;
  onClose: () => void;
  account: ProfileBankAccountMasked;
}

function DeleteBankAccountDialog({ open, onClose, account }: DeleteBankAccountDialogProps) {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const deleteAccount = useDeleteMyBankAccount();
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const normalizedReason = reason.trim();

  const handleDelete = () => {
    setPasswordError(null);
    if (!password) {
      setPasswordError(t('common:validation.required'));
      return;
    }
    if (normalizedReason.length < 10) return;
    deleteAccount.mutate(
      { id: account.id, input: { currentPassword: password, reason: normalizedReason } },
      {
        onSuccess: () => {
          toast.success(t('auth:profile.bank_accounts.toast.deleted'));
          setPassword('');
          setReason('');
          onClose();
        },
        onError: (err) => {
          const { status, code, message } = extractApiError(err);
          if (status === 401 && code === 'AUTH_INVALID_CREDENTIALS') {
            setPasswordError(t('auth:profile.bank_accounts.errors.wrong_password'));
            return;
          }
          toast.error(
            t(`errors:${code ?? 'generic'}`, {
              defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setPassword('');
          setReason('');
          setPasswordError(null);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('auth:profile.bank_accounts.delete')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t('auth:profile.bank_accounts.delete_confirm')}</p>
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
            <ShieldAlert className="size-4 text-amber-600 mt-0.5 shrink-0" aria-hidden />
            <p className="text-xs text-amber-800">
              {t('auth:profile.bank_accounts.security_notice')}
            </p>
          </div>
          <Input
            type="password"
            label={t('auth:profile.bank_accounts.fields.current_password')}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError ?? undefined}
          />
          <div className="space-y-1.5">
            <label
              htmlFor={`bank-account-delete-reason-${account.id}`}
              className="text-sm font-medium text-secondary-900"
            >
              {t('common:deletion.reason_label')}
            </label>
            <Textarea
              id={`bank-account-delete-reason-${account.id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('common:deletion.reason_placeholder')}
              maxLength={500}
            />
            <p className="text-xs text-secondary-600">
              {t('common:deletion.min_reason', { count: 10 })}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={deleteAccount.isPending}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-error-500 hover:bg-error-600"
            onClick={handleDelete}
            disabled={deleteAccount.isPending || normalizedReason.length < 10}
            aria-busy={deleteAccount.isPending || undefined}
          >
            {deleteAccount.isPending && <Spinner size="sm" />}
            {t('common:actions.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BankAccountSection() {
  const { t } = useTranslation(['auth', 'common']);
  const { data: accounts, isLoading } = useMyBankAccounts();
  const updateAccount = useUpdateMyBankAccount();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    account?: ProfileBankAccountMasked;
    makePrimary?: boolean;
  }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<ProfileBankAccountMasked | null>(null);

  const handleSetPrimary = (account: ProfileBankAccountMasked) => {
    if (account.isPrimary) return;
    // Requires password — open edit dialog so user can confirm with password
    setDialogState({ open: true, account, makePrimary: true });
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading font-bold text-secondary-950">
          {t('auth:profile.sections.bank_accounts')}
        </h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setDialogState({ open: true })}
          className="min-h-[44px]"
        >
          <Plus className="size-4" aria-hidden />
          {t('auth:profile.bank_accounts.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
        </div>
      ) : !accounts || accounts.length === 0 ? (
        <p className="text-sm text-slate-500">{t('auth:profile.bank_accounts.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 space-y-0">
          {accounts.map((account) => (
            <li key={account.id} className="py-3 flex items-start gap-3">
              <CreditCard className="size-4 text-slate-400 mt-0.5 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-body font-medium text-secondary-950">
                    {account.bankName} ({account.bankCode})
                  </span>
                  {account.isPrimary && (
                    <Badge variant="info" className="text-xs">
                      {t('auth:profile.bank_accounts.primary_badge')}
                    </Badge>
                  )}
                  {account.isVerified && (
                    <span className="flex items-center gap-1 text-xs text-emerald-700">
                      <CheckCircle className="size-3" aria-hidden />
                      {t('auth:profile.bank_accounts.verified_badge')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-0.5">
                  {t(`auth:profile.bank_accounts.type_${account.accountType}`)}
                  {account.agencyLast4
                    ? ` · ${t('auth:profile.bank_accounts.masked_agency', { last4: account.agencyLast4 })}`
                    : ''}
                  {account.accountNumberLast4
                    ? ` / ${t('auth:profile.bank_accounts.masked_account', { last4: account.accountNumberLast4 })}`
                    : ''}
                </p>
                <p className="text-xs text-slate-500">{account.holderName}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!account.isPrimary && (
                  <button
                    type="button"
                    title={t('auth:profile.bank_accounts.set_primary')}
                    onClick={() => handleSetPrimary(account)}
                    disabled={updateAccount.isPending}
                    className="p-2 rounded-md text-slate-400 hover:text-primary-600 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Star className="size-4" aria-hidden />
                    <span className="sr-only">{t('auth:profile.bank_accounts.set_primary')}</span>
                  </button>
                )}
                <button
                  type="button"
                  title={t('auth:profile.bank_accounts.edit')}
                  onClick={() => setDialogState({ open: true, account })}
                  className="p-2 rounded-md text-slate-400 hover:text-secondary-700 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Pencil className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.bank_accounts.edit')}</span>
                </button>
                <button
                  type="button"
                  title={t('auth:profile.bank_accounts.delete')}
                  onClick={() => setDeleteTarget(account)}
                  className="p-2 rounded-md text-slate-400 hover:text-error-600 hover:bg-red-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Trash2 className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.bank_accounts.delete')}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialogState.open && (
        <BankAccountDialog
          open={dialogState.open}
          onClose={() => setDialogState({ open: false })}
          account={dialogState.account}
          accountCount={accounts?.length ?? 0}
          makePrimary={dialogState.makePrimary}
        />
      )}

      {deleteTarget && (
        <DeleteBankAccountDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          account={deleteTarget}
        />
      )}
    </section>
  );
}
